import React from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import { Phone, CheckCircle, Clock, Shield, MapPin, AlertTriangle, ArrowRight } from 'lucide-react';
import Breadcrumbs from '../components/Breadcrumbs';

const TueroeffnungVerschlossenPage = () => {
  const phone = '+4923139085116';
  const displayPhone = '0231 39085116';

  const faqData = [
    {
      question: 'Muss ich bei Schlüsselverlust immer den Zylinder tauschen?',
      answer: 'Aus Sicherheitsgründen empfehlen wir dringend einen Zylindertausch, wenn Schlüssel verloren oder gestohlen wurden. So verhindern Sie unbefugten Zugang zu Ihrer Wohnung.'
    },
    {
      question: 'Wie läuft die Legitimation ab?',
      answer: 'Wir benötigen einen Nachweis, dass Sie berechtigt sind, die Wohnung zu betreten – z.B. Personalausweis mit passender Adresse, Mietvertrag oder Nachbarn als Zeugen.'
    },
    {
      question: 'Kann ich bar und mit Karte zahlen?',
      answer: 'Ja, wir akzeptieren Barzahlung, EC-Karten und alle gängigen Kreditkarten (Visa, Mastercard).'
    },
    {
      question: 'Was kostet ein zusätzlicher Zylindertausch?',
      answer: 'Die Kosten für einen neuen Schließzylinder hängen von der Qualität und Sicherheitsstufe ab. Wir besprechen dies vorab transparent mit Ihnen.'
    }
  ];

  return (
    <>
      <Helmet>
        <title>Türöffnung verschlossene Tür Dortmund – Hilfe bei Schlüsselverlust & defektem Schloss</title>
        <meta name="description" content="Ihre Tür ist abgeschlossen und der Schlüssel ist weg oder defekt? Schlüsseldienst Dortmund öffnet verschlossene Türen fachgerecht & fair. Anrufen: 0231 39085116." />
        <link rel="canonical" href="https://schluesseldienst.services/leistungen/tueroeffnung-verschlossen/" />
      </Helmet>

      <div className="container mx-auto px-4 py-12">
        <Breadcrumbs items={[
          { label: 'Leistungen', path: '/leistungen' },
          { label: 'Türöffnung verschlossen' }
        ]} />
        
        <h1 className="text-4xl sm:text-5xl font-bold mb-8 text-gray-900">
          Türöffnung bei verschlossener Tür in Dortmund
        </h1>

        <div className="max-w-4xl mb-12">
          <p className="text-lg text-gray-700 mb-4">
            <strong>Schlüssel verloren? Tasche gestohlen? Schlüssel im Schloss abgebrochen?</strong> Bei einer verschlossenen Tür ist die Situation 
            komplexer als bei einer nur zugefallenen Tür – aber kein Grund zur Panik.
          </p>
          <p className="text-lg text-gray-700 mb-4">
            Als professioneller Schlüsseldienst in Dortmund öffnen wir auch verschlossene Türen fachgerecht und beraten Sie anschließend, 
            ob aus Sicherheitsgründen ein Schlosswechsel sinnvoll ist.
          </p>
          <p className="text-lg text-gray-700 mb-6">
            Die Schössow Dienstleistungen UG steht für Transparenz, faire Preise und schnelle Hilfe – 24 Stunden am Tag, 7 Tage die Woche.
          </p>
          
          <div className="bg-orange-50 border-2 border-orange-200 rounded-xl p-6 mb-8">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Sofort Hilfe benötigt?</h3>
                <p className="text-gray-700">Wir sind 24/7 für Sie da – schnelle Anfahrt garantiert!</p>
              </div>
              <a
                href={`tel:${phone}`}
                className="inline-flex items-center gap-2 bg-orange-600 hover:bg-orange-700 text-white px-6 py-3 rounded-full font-bold text-lg transition-all shadow-lg whitespace-nowrap"
              >
                <Phone size={20} />
                {displayPhone}
              </a>
            </div>
          </div>
        </div>

        <div className="max-w-4xl mb-12">
          <h2 className="text-3xl font-bold mb-6 text-gray-900">Vorteile auf einen Blick</h2>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="flex gap-3">
              <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
              <p className="text-gray-700"><strong>Fachgerechte Öffnung</strong> auch bei verschlossenen Türen</p>
            </div>
            <div className="flex gap-3">
              <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
              <p className="text-gray-700"><strong>Beratung zum Schlosswechsel</strong> aus Sicherheitsgründen</p>
            </div>
            <div className="flex gap-3">
              <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
              <p className="text-gray-700"><strong>Transparente Festpreise</strong> ohne versteckte Kosten</p>
            </div>
            <div className="flex gap-3">
              <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
              <p className="text-gray-700"><strong>Legitimationsprüfung</strong> zu Ihrem Schutz</p>
            </div>
            <div className="flex gap-3">
              <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
              <p className="text-gray-700"><strong>24/7 Notdienst</strong> in ganz Dortmund</p>
            </div>
            <div className="flex gap-3">
              <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
              <p className="text-gray-700"><strong>EC- und Kartenzahlung</strong> möglich</p>
            </div>
          </div>
        </div>

        <div className="max-w-4xl mb-12">
          <h2 className="text-3xl font-bold mb-6 text-gray-900">Wann muss das Schloss gewechselt werden?</h2>
          <div className="bg-amber-50 border-l-4 border-amber-500 p-6 rounded-lg mb-6">
            <div className="flex gap-3">
              <AlertTriangle className="w-6 h-6 text-amber-600 flex-shrink-0" />
              <div>
                <h3 className="font-bold text-lg mb-2 text-gray-900">Wichtig für Ihre Sicherheit:</h3>
                <p className="text-gray-700 mb-3">
                  Wenn Schlüssel verloren oder gestohlen wurden, empfehlen wir dringend einen Zylindertausch. 
                  Nur so können Sie sicher sein, dass niemand unbefugt Zugang zu Ihrer Wohnung erhält.
                </p>
                <p className="text-gray-700">
                  Bei einem abgebrochenen Schlüssel im Schloss ist ein Austausch meist nicht nötig – 
                  wir können den Schlüsselrest entfernen und Sie können einen Ersatzschlüssel anfertigen lassen.
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-4xl mb-12">
          <h2 className="text-3xl font-bold mb-6 text-gray-900">Preise & Kosten</h2>
          <div className="bg-gradient-to-br from-orange-50 to-amber-50 rounded-xl p-8 border-2 border-orange-200">
            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <div className="bg-white rounded-lg p-6 shadow-md">
                <div className="flex items-center gap-2 mb-3">
                  <Clock className="w-6 h-6 text-orange-600" />
                  <h3 className="text-xl font-bold">Mo–Fr 08–20 Uhr</h3>
                </div>
                <div className="text-4xl font-bold text-orange-600 mb-2">119 €</div>
                <p className="text-gray-700">Türöffnung + Anfahrt</p>
              </div>
              
              <div className="bg-white rounded-lg p-6 shadow-md">
                <div className="flex items-center gap-2 mb-3">
                  <Clock className="w-6 h-6 text-orange-600" />
                  <h3 className="text-xl font-bold">Sa–So & Feiertage</h3>
                </div>
                <div className="text-4xl font-bold text-orange-600 mb-2">199 €</div>
                <p className="text-gray-700">Türöffnung + Anfahrt</p>
              </div>
            </div>
            
            <div className="bg-white rounded-lg p-6">
              <h4 className="font-bold text-lg mb-3">Zusätzliche Kosten nur bei Material</h4>
              <p className="text-gray-700 mb-3">
                Wenn Sie sich für einen Zylindertausch entscheiden, kommen die Kosten für das neue Schloss hinzu. 
                Diese besprechen wir vorab transparent mit Ihnen. Keine versteckten Gebühren!
              </p>
            </div>
          </div>
        </div>

        <div className="max-w-4xl mb-12">
          <h2 className="text-3xl font-bold mb-6 text-gray-900">Sicherheit nach der Öffnung</h2>
          <div className="bg-white rounded-lg shadow-md p-6">
            <p className="text-gray-700 mb-4">
              Nach der Türöffnung beraten wir Sie ausführlich zu Ihrer Sicherheit. Besonders bei Schlüsselverlust 
              ist ein Zylindertausch aus folgenden Gründen wichtig:
            </p>
            <ul className="space-y-2">
              <li className="flex gap-2">
                <Shield className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                <span className="text-gray-700">Verhindern Sie unbefugten Zugang zu Ihrer Wohnung</span>
              </li>
              <li className="flex gap-2">
                <Shield className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                <span className="text-gray-700">Schutz vor Missbrauch durch den Finder</span>
              </li>
              <li className="flex gap-2">
                <Shield className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                <span className="text-gray-700">Beruhigung für Ihr Sicherheitsgefühl</span>
              </li>
            </ul>
            <div className="mt-6">
              <Link 
                to="/leistungen/schlosswechsel"
                className="inline-flex items-center gap-2 bg-orange-600 hover:bg-orange-700 text-white px-6 py-3 rounded-full font-semibold transition-all"
              >
                Mehr zum Schlosswechsel
                <ArrowRight size={18} />
              </Link>
            </div>
          </div>
        </div>

        <div className="max-w-4xl mb-12">
          <h2 className="text-3xl font-bold mb-6 text-gray-900">Einsatzgebiet in Dortmund</h2>
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex gap-3 mb-4">
              <MapPin className="w-6 h-6 text-orange-600 flex-shrink-0" />
              <p className="text-gray-700">
                <strong>In allen 52 Stadtteilen von Dortmund im Einsatz.</strong> Schnelle Anfahrt garantiert!
              </p>
            </div>
          </div>
        </div>

        <div className="max-w-4xl mb-12">
          <h2 className="text-3xl font-bold mb-6 text-gray-900">Häufige Fragen</h2>
          <div className="space-y-4">
            {faqData.map((faq, index) => (
              <div key={index} className="bg-white rounded-lg shadow-md p-6">
                <h3 className="text-xl font-bold mb-3 text-gray-900">{faq.question}</h3>
                <p className="text-gray-700">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-gray-900 text-white rounded-2xl p-8 text-center">
          <h2 className="text-3xl font-bold mb-4">Schlüssel verloren? Jetzt anrufen!</h2>
          <p className="text-lg mb-6 text-gray-300">
            24/7 Notdienst • Festpreise • Fachgerechte Öffnung
          </p>
          <a
            href={`tel:${phone}`}
            className="inline-flex items-center gap-3 bg-orange-600 hover:bg-orange-700 text-white px-8 py-4 rounded-full font-bold text-xl transition-all shadow-lg hover:shadow-xl"
          >
            <Phone size={24} />
            {displayPhone}
          </a>
        </div>

        <div className="mt-12 max-w-4xl">
          <h3 className="text-xl font-bold mb-4 text-gray-900">Weitere Leistungen</h3>
          <div className="flex flex-wrap gap-4">
            <Link to="/leistungen" className="inline-flex items-center gap-2 bg-gray-100 hover:bg-gray-200 text-gray-800 px-6 py-3 rounded-full font-semibold transition-all">
              Alle Leistungen <ArrowRight size={18} />
            </Link>
            <Link to="/leistungen/schlosswechsel" className="inline-flex items-center gap-2 bg-gray-100 hover:bg-gray-200 text-gray-800 px-6 py-3 rounded-full font-semibold transition-all">
              Schlosswechsel <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </div>
    </>
  );
};

export default TueroeffnungVerschlossenPage;
